import { useEffect, useLayoutEffect, useRef } from 'react';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { ReplanningStageId } from '~/state/atoms/adaptiveReplanningAtoms';
import { useRunScans } from './useRunScans';

// Stages where the target is fixed as the primary scan (only Plan & Target may change the roles).
const ORIENTATION_LOCKED: readonly ReplanningStageId[] = [
    'register',
    'registration-qa',
    'review',
];

// Snaps the target back to the primary image if a later stage's control swapped it.
export function useLockReplanningOrientation(stageId: ReplanningStageId): void {
    const patient = useSelectedPatient();
    const primaryImage = useSelectedPrimaryImage();
    const { target } = useRunScans();
    const { setEnvironmentPrimaryImage } = useViewportEnvironment();

    const apply = useRef(setEnvironmentPrimaryImage);
    useLayoutEffect(() => {
        apply.current = setEnvironmentPrimaryImage;
    });

    const locked = ORIENTATION_LOCKED.includes(stageId);
    const deviates =
        !!target &&
        !!primaryImage &&
        primaryImage.seriesUid !== target.seriesUid;

    useEffect(() => {
        if (!locked || !patient || !target || !deviates) {
            return;
        }
        apply.current([target], patient.dataSet.PatientID);
    }, [locked, patient, target, deviates]);
}
